import { useState, useEffect, useRef } from "react";
import SignatureCanvas from "react-signature-canvas";
import { trimmedSignatureFromPad } from "@/lib/signatureCapture";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, RotateCcw, Check, AlertTriangle } from "lucide-react";
import { format } from "date-fns";

const emptySignature = {
  name: "",
  signature_data: "",
  date: format(new Date(), "yyyy-MM-dd"),
};

export const StepDeclarations = ({ formData, updateFormData }) => {
  const [borrowerSignatures, setBorrowerSignatures] = useState(
    formData.declaration_signatures.borrower_signatures.length > 0
      ? formData.declaration_signatures.borrower_signatures
      : [{ ...emptySignature }]
  );
  const [guarantorSignatures, setGuarantorSignatures] = useState(
    formData.declaration_signatures.guarantor_signatures.length > 0
      ? formData.declaration_signatures.guarantor_signatures
      : formData.guarantors.map(() => ({ ...emptySignature }))
  );
  const [acknowledgedDeclaration, setAcknowledgedDeclaration] = useState(Boolean(formData.declarations?.confirmed_true_correct));
  const [acknowledgedPrivacy, setAcknowledgedPrivacy] = useState(Boolean(formData.declarations?.privacy_acknowledged));
  
  const borrowerSignatureRefs = useRef([]);
  const guarantorSignatureRefs = useRef([]);

  useEffect(() => {
    updateFormData("declaration_signatures", {
      borrower_signatures: borrowerSignatures,
      guarantor_signatures: guarantorSignatures,
    });
  }, [borrowerSignatures, guarantorSignatures, updateFormData]);

  useEffect(() => {
    updateFormData("declarations", {
      confirmed_true_correct: acknowledgedDeclaration,
      privacy_acknowledged: acknowledgedPrivacy,
    });
  }, [acknowledgedDeclaration, acknowledgedPrivacy, updateFormData]);

  // Pre-fill guarantor names if available
  useEffect(() => {
    if (formData.guarantors.length > 0 && guarantorSignatures.length === 0) {
      const prefilledSignatures = formData.guarantors.map((g) => ({
        ...emptySignature,
        name: `${g.given_names} ${g.surname}`.trim(),
      }));
      setGuarantorSignatures(prefilledSignatures);
    }
  }, [formData.guarantors, guarantorSignatures.length]);

  const handleBorrowerSignatureChange = (index, field, value) => {
    const newSignatures = [...borrowerSignatures];
    newSignatures[index] = { ...newSignatures[index], [field]: value };
    setBorrowerSignatures(newSignatures);
  };

  const handleGuarantorSignatureChange = (index, field, value) => {
    const newSignatures = [...guarantorSignatures];
    newSignatures[index] = { ...newSignatures[index], [field]: value };
    setGuarantorSignatures(newSignatures);
  };

  const clearBorrowerSignature = (index) => {
    if (borrowerSignatureRefs.current[index]) {
      borrowerSignatureRefs.current[index].clear();
      handleBorrowerSignatureChange(index, "signature_data", "");
    }
  };

  const clearGuarantorSignature = (index) => {
    if (guarantorSignatureRefs.current[index]) {
      guarantorSignatureRefs.current[index].clear();
      handleGuarantorSignatureChange(index, "signature_data", "");
    }
  };

  // SIG-LEGIBILITY-1 (2026-08-26) — trim to the ink instead of exporting the
  // whole pad. Same fault as the e-sign pads: toDataURL() was storing ~89%
  // blank pixels on every declaration signature, and whatever renders them
  // later scales that padding rather than the signature. See
  // lib/signatureCapture.js.
  const saveBorrowerSignature = (index) => {
    const dataUrl = trimmedSignatureFromPad(borrowerSignatureRefs.current[index]);
    if (dataUrl) handleBorrowerSignatureChange(index, "signature_data", dataUrl);
  };

  const saveGuarantorSignature = (index) => {
    const dataUrl = trimmedSignatureFromPad(guarantorSignatureRefs.current[index]);
    if (dataUrl) handleGuarantorSignatureChange(index, "signature_data", dataUrl);
  };

  const addBorrowerSignature = () => {
    setBorrowerSignatures([...borrowerSignatures, { ...emptySignature }]);
  };

  const removeBorrowerSignature = (index) => {
    if (borrowerSignatures.length > 1) {
      setBorrowerSignatures(borrowerSignatures.filter((_, i) => i !== index));
    }
  };

  const addGuarantorSignature = () => {
    setGuarantorSignatures([...guarantorSignatures, { ...emptySignature }]);
  };

  const removeGuarantorSignature = (index) => {
    if (guarantorSignatures.length > 1) {
      setGuarantorSignatures(guarantorSignatures.filter((_, i) => i !== index));
    }
  };

  const renderSignatureBlock = (signature, index, isBorrower) => {
    const refs = isBorrower ? borrowerSignatureRefs : guarantorSignatureRefs;
    const handleChange = isBorrower ? handleBorrowerSignatureChange : handleGuarantorSignatureChange;
    const clearSignature = isBorrower ? clearBorrowerSignature : clearGuarantorSignature;
    const saveSignature = isBorrower ? saveBorrowerSignature : saveGuarantorSignature;
    const removeSignature = isBorrower ? removeBorrowerSignature : removeGuarantorSignature;
    const signatures = isBorrower ? borrowerSignatures : guarantorSignatures;
    const type = isBorrower ? "borrower" : "guarantor";

    return (
      <div 
        key={index} 
        className="p-6 bg-white border border-gray-200 rounded mb-4"
        data-testid={`${type}-signature-block-${index}`}
      >
        <div className="flex justify-between items-start mb-4">
          <h5 className="font-medium text-[#021d49]">
            {isBorrower ? `Borrower ${index + 1}` : `Guarantor ${index + 1}`}
          </h5>
          {signatures.length > 1 && (
            <button
              onClick={() => removeSignature(index)}
              className="text-red-500 hover:text-red-700 p-1"
              data-testid={`remove-${type}-signature-${index}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <Label className="form-label form-label-required">Full Name</Label>
            <Input
              value={signature.name}
              onChange={(e) => handleChange(index, "name", e.target.value)}
              placeholder="Enter full legal name"
              className="form-input"
              data-testid={`${type}-name-${index}`}
            />
          </div>
          <div>
            <Label className="form-label form-label-required">Date</Label>
            <Input
              type="date"
              value={signature.date}
              onChange={(e) => handleChange(index, "date", e.target.value)}
              className="form-input"
              data-testid={`${type}-date-${index}`}
            />
          </div>
        </div>

        <div>
          <Label className="form-label form-label-required">Signature</Label>
          <div className="signature-container">
            {signature.signature_data ? (
              <div className="relative">
                <img 
                  src={signature.signature_data} 
                  alt="Signature" 
                  className="w-full h-32 object-contain bg-white border border-gray-200 rounded"
                />
                <button
                  onClick={() => {
                    handleChange(index, "signature_data", "");
                  }}
                  className="absolute top-2 right-2 p-2 bg-white rounded shadow hover:bg-gray-100"
                  data-testid={`${type}-clear-saved-signature-${index}`}
                >
                  <RotateCcw className="w-4 h-4 text-gray-600" />
                </button>
                <div className="absolute bottom-2 right-2 flex items-center gap-1 px-2 py-1 bg-green-100 rounded text-green-700 text-xs">
                  <Check className="w-3 h-3" />
                  Signed
                </div>
              </div>
            ) : (
              <>
                {/* SIG-LEGIBILITY-1 (2026-08-26) — backgroundColor="white" was
                    REMOVED from this pad. It baked an opaque white rectangle
                    into the exported PNG, which then sits as a white box on top
                    of whatever renders the signature later. The pad still reads
                    as white to the user via the surrounding card. Don't put it
                    back. */}
                <div className="mb-2">
                  <SignatureCanvas
                    ref={(ref) => (refs.current[index] = ref)}
                    canvasProps={{
                      className: "signature-canvas w-full h-32",
                      "data-testid": `${type}-signature-canvas-${index}`,
                    }}
                    penColor="#021d49"
                  />
                </div>
                <div className="flex justify-between items-center">
                  <p className="text-xs text-gray-500">Sign in the box above, then click "Save Signature"</p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => clearSignature(index)}
                      className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 px-3 py-1 border border-gray-300 rounded"
                      data-testid={`${type}-clear-signature-${index}`}
                    >
                      <RotateCcw className="w-3 h-3" />
                      Clear
                    </button>
                    <button
                      onClick={() => saveSignature(index)}
                      className="text-sm text-white bg-[#bd9a59] hover:bg-[#9c7e46] flex items-center gap-1 px-4 py-1 rounded font-medium"
                      data-testid={`${type}-save-signature-${index}`}
                    >
                      <Check className="w-3 h-3" />
                      Save Signature
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-10">
      {/* Business Purpose Declaration */}
      <section className="form-card rounded">
        <h3 className="section-subheader">Business Purpose Declaration</h3>
        
        <div className="declaration-box">
          <p className="text-sm text-gray-700 mb-4">
            By signing this Application Form, I/We confirm that I/we are applying to Solara Global for credit that is wholly or predominantly for business or investment purposes, and I acknowledge that the loan is not subject to the National Consumer Credit Protections Act 2009.
          </p>
          <p className="text-sm text-gray-700">
            In this declaration, "You" and "Your" means both the Borrower and Guarantor joint and severally.
          </p>
        </div>

        <div className="space-y-4 mt-6">
          <h4 className="font-medium text-[#021d49]">1. Commissions Disclosure Declaration</h4>
          <p className="text-sm text-gray-600">
            From time to time, Solara Global may pay a commission or fee to a third party such as a broker or advisor who has introduced You to Us. The amount of any such fee or commission will be disclosed to You in writing prior to accepting the Loan and will be deducted from the proceeds of the loan amount.
          </p>
        </div>

        <div className="space-y-4 mt-6">
          <h4 className="font-medium text-[#021d49]">2. General Agreement</h4>
          <p className="text-sm text-gray-600 mb-2">By signing and/or initialling this application form, you agree:</p>
          <ul className="list-disc list-inside text-sm text-gray-600 space-y-2 ml-4">
            <li>All information contained in the Form is true and correct</li>
            <li>Solara Global is under no obligation to lend to you</li>
            <li>That the properties identified herein are offered as security</li>
            <li>You will execute security documents to give effect to the loan</li>
            <li>Solara Global will rely on the information provided</li>
            <li>If you act in a deceitful or dishonest manner, you will be liable for criminal prosecution</li>
            <li>You have had the opportunity to obtain independent financial advice</li>
            <li>You were not involved in the origination or structuring of the proposed finance</li>
          </ul>
        </div>

        <div className="flex items-start gap-3 mt-6 p-4 bg-gray-50 rounded">
          <Checkbox
            id="acknowledge-declaration"
            checked={acknowledgedDeclaration}
            onCheckedChange={setAcknowledgedDeclaration}
            data-testid="acknowledge-declaration-checkbox"
          />
          <Label htmlFor="acknowledge-declaration" className="cursor-pointer text-sm">
            I have read and understood the Business Purpose Declaration and General Agreement above.
          </Label>
        </div>
      </section>

      {/* Privacy Act Declaration */}
      <section className="form-card rounded">
        <h3 className="section-subheader">Privacy Act and Declaration</h3>
        
        <div className="declaration-box">
          <p className="text-sm text-gray-700 mb-4">
            I/We acknowledge the offer of Solara Global and hereby agree to the use of information provided in this Application Form being used by Solara Global and any other financiers for the purposes of assessing the information, and providing credit to me/us on such terms as may be approved.
          </p>
          
          <p className="text-sm text-gray-700 mb-2">I/We authorize Solara Global and any other financier(s) to:</p>
          <ol className="list-decimal list-inside text-sm text-gray-600 space-y-2 ml-4">
            <li>Seek and use commercial credit information from credit reporting agencies</li>
            <li>Seek and use consumer information in relation to this credit application</li>
            <li>Obtain information from any other person to collect overdue payments</li>
            <li>Exchange information about my/our credit arrangements with other credit providers</li>
            <li>Exchange information with advisers, agents, or contractors</li>
            <li>Provide information to credit reporting agencies about any defaults</li>
            <li>Provide information for securitisation and/or funding purposes</li>
            <li>Provide information to guarantors about my/our loan application</li>
            <li>Disclose personal information as required by law</li>
          </ol>
        </div>

        <div className="flex items-start gap-3 mt-6 p-4 bg-gray-50 rounded">
          <Checkbox
            id="acknowledge-privacy"
            checked={acknowledgedPrivacy}
            onCheckedChange={setAcknowledgedPrivacy}
            data-testid="acknowledge-privacy-checkbox"
          />
          <Label htmlFor="acknowledge-privacy" className="cursor-pointer text-sm">
            I have read and understood the Privacy Act Declaration and authorize the above actions.
          </Label>
        </div>
      </section>

      {/* Warnings */}
      {(!acknowledgedDeclaration || !acknowledgedPrivacy) && (
        <div className="p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-yellow-800">
            Please acknowledge both declarations above before signing the application.
          </p>
        </div>
      )}

      {/* Borrower Signatures */}
      <section className="form-card rounded">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="section-subheader mb-0">Signature of Borrower(s)</h3>
            <p className="text-sm text-gray-500">To be signed by each Borrower</p>
          </div>
          <button
            onClick={addBorrowerSignature}
            className="btn-secondary px-3 py-2 rounded text-sm flex items-center gap-1"
            data-testid="add-borrower-signature-btn"
          >
            <Plus className="w-4 h-4" />
            Add Borrower
          </button>
        </div>

        {borrowerSignatures.map((sig, index) => renderSignatureBlock(sig, index, true))}
      </section>

      {/* Guarantor Signatures */}
      <section className="form-card rounded">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="section-subheader mb-0">Signature of Guarantor(s)</h3>
            <p className="text-sm text-gray-500">To be signed by each Guarantor</p>
          </div>
          <button
            onClick={addGuarantorSignature}
            className="btn-secondary px-3 py-2 rounded text-sm flex items-center gap-1"
            data-testid="add-guarantor-signature-btn"
          >
            <Plus className="w-4 h-4" />
            Add Guarantor
          </button>
        </div>

        {guarantorSignatures.map((sig, index) => renderSignatureBlock(sig, index, false))}
      </section>
    </div>
  );
};
